import { FileTypes } from 'src/features/common/components/file-upload/file-upload';
import { compressImage } from 'src/helpers/jpeg-compression';
import { DocumentWithOptionalBody } from '../pages/upload-documents/upload-documents';

/**
 * Converts base64 file to js File object
 * @param {string} base64 - The base64 string
 * @param {string} filename - The name to give the file
 * @param {string} mime - The mime type of the file
 * @returns {File}
 */
export const base64ToFile = (base64: string, filename: string, mime: FileTypes): File => {
  let base64String = atob(base64);
  let base64StrLength = base64String.length;
  let u8Array = new Uint8Array(base64StrLength);

  while (base64StrLength--) {
    u8Array[base64StrLength] = base64String.charCodeAt(base64StrLength);
  }

  return new File([u8Array], filename, { type: mime });
};

type FileType = {
  ext?: string;
  mime: FileTypes;
};

/**
 * Gets the file type of a base64 file
 * @param {string} base64 - The base64 string
 * @returns {FileType}
 */
export const getFileType = (base64: string): FileType => {
  //Get messageType
  let fileType: FileType = { mime: FileTypes.jpeg };
  //check for JPEG ['application/pdf', 'image/jpeg']
  const regex = new RegExp('(?=^(?:.{4})*$)^JVBER[a-zA-Z0-9+/]*={0,3}$');
  if (base64.charAt(0) === '/') {
    fileType = { ext: '.jpg', mime: FileTypes.jpeg };
  } else if (regex.test(base64)) {
    //Check for pdf
    fileType = { ext: '.pdf', mime: FileTypes.pdf };
  }
  return fileType;
};

/**
 * Get the base64 data portion of a URL encoded string
 * @param {string} urlEncodedString - The URL encoded string
 * @returns {string}
 */
export const getBase64DataOnly = (urlEncodedString: string): string =>
  urlEncodedString ? urlEncodedString.split(',')[1] : '';

/**
 *  Compress all the files if they are JPEG
 * @param {File[]} files - The files to compress
 * @param compressOptions - The compression options to use
 * @returns {void}
 */
export const compressIfJpegCached = (
  files: File[],
  compressOptions: { maxSizeMB: number; maxWidthOrHeight?: number },
) =>
  files.map((file) => {
    if (file.type === 'image/jpeg') {
      return compressImage(file, compressOptions);
    } else {
      return file;
    }
  });

/**
 * Get the file types in a user friendly manner.
 * @param {FileTypes[]} acceptedFileTypes - The file types
 * @param {string} separator - The separator to use to between file types
 * @returns {string}
 */
export const fileTypes = (fileTypes: FileTypes[], separator: string = '/'): string => {
  return fileTypes
    .map((fileType) => fileType.substring(fileType.indexOf('/') + 1).toUpperCase())
    .join(separator);
};

/**
 * Gets the file names of the documents passed
 * @param {DocumentWithOptionalBody[]} documents - The documents
 * @returns {string[]}
 */
export const getFileNames = (documents: DocumentWithOptionalBody[]): string[] => {
  const fileNames: string[] = [];
  if (documents.length > 0) {
    documents.forEach((element) => {
      if (element.idNumber === 'TBD' || element.documentName === 'TBD') {
        fileNames.push(element.documentName ?? '');
      }
    });
  }
  return fileNames;
};
